import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Label } from '../ui/Label';
import { Checkbox } from '../ui/Checkbox';
import { cn } from '../../lib/utils';

// ----------------------------------------------------------------------

type RHFSelectProps = {
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  children?: React.ReactNode;
  options?: { label: string; value: string }[];
  className?: string;
  disabled?: boolean;
};

export function RHFSelect({
  name,
  label,
  placeholder,
  helperText,
  children,
  options,
  className,
  disabled
}: RHFSelectProps) {
  const { control } = useFormContext();

  const extractedOptions = React.useMemo(() => {
    if (options) return options;
    if (!children) return [];

    const opts: { label: string; value: string }[] = [];
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const value = child.props.value as string;
        // Skip empty/placeholder values
        if (value !== '' && value !== undefined) {
          const optLabel = (child.props.children as string) || value;
          opts.push({ label: String(optLabel), value: String(value) });
        }
      }
    });
    return opts;
  }, [children, options]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div className={cn('w-full space-y-1', className)}>
          {label && (
            <Label htmlFor={name} className={cn(error && 'text-destructive')}>
              {label}
            </Label>
          )}
          <Select value={field.value ?? ''} onValueChange={field.onChange} disabled={disabled}>
            <SelectTrigger id={name} error={!!error} className="w-full">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {extractedOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(error || helperText) && (
            <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
              {error ? error.message : helperText}
            </p>
          )}
        </div>
      )}
    />
  );
}

// ----------------------------------------------------------------------

type RHFMultiSelectProps = {
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  options: { label: string; value: string }[];
  className?: string;
  disabled?: boolean;
};

export function RHFMultiSelect({
  name,
  label,
  placeholder,
  helperText,
  options,
  className,
  disabled
}: RHFMultiSelectProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const selectedValues: string[] = field.value || [];

        const toggleValue = (value: string) => {
          const newValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];
          field.onChange(newValues);
        };

        const selectedLabels = options
          .filter((opt) => selectedValues.includes(opt.value))
          .map((opt) => opt.label)
          .join(', ');

        return (
          <div className={cn('w-full space-y-1', className)}>
            {label && (
              <Label htmlFor={name} className={cn(error && 'text-destructive')}>
                {label}
              </Label>
            )}
            <div
              className={cn(
                'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm',
                error && 'border-destructive',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              {selectedLabels || <span className="text-muted-foreground">{placeholder}</span>}
            </div>
            <div className="space-y-1 pl-1">
              {options.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${name}-${option.value}`}
                    checked={selectedValues.includes(option.value)}
                    onCheckedChange={() => toggleValue(option.value)}
                    disabled={disabled}
                  />
                  <Label
                    htmlFor={`${name}-${option.value}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            {(error || helperText) && (
              <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
                {error ? error.message : helperText}
              </p>
            )}
          </div>
        );
      }}
    />
  );
}
